import { useEffect, useState, useCallback, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import useBenguide from 'hooks/benguide';
import {
  addMessage,
  setPlanRecommenderContext,
} from 'modules/chatbot/slices/chatbotSlice';
import {
  ChatMessage,
  MessageStatus,
} from 'modules/chatbot/types/chatbot.types';
import { Sender } from 'modules/chatbot/enums/Sender';

interface SyncData {
  type: 'PLAN_RECOMMENDER_SYNC';
  timestamp: number;
  plans: any[];
  answers: any[];
  questions: any[];
  benguideHash: string;
  actionMessage: Omit<
    ChatMessage,
    'id' | 'timestamp' | 'createdAt' | 'updatedAt'
  >;
}

interface TabPresenceData {
  type: 'TAB_PRESENCE';
  timestamp: number;
  tabId: string;
  benguideHash: string;
}

interface FocusRequestData {
  type: 'FOCUS_REQUEST';
  timestamp: number;
  benguideHash: string;
  requestingTabId: string;
}

interface FocusResponseData {
  type: 'FOCUS_RESPONSE';
  timestamp: number;
  benguideHash: string;
  respondingTabId: string;
}

/**
 * Manual tab synchronization hook for plan recommender data with cooldown
 *
 * Features:
 * - Detects when other tabs with the same benefit guide are open
 * - Handles manual sync (button click only)
 * - 30-second cooldown period after each sync to prevent spam
 * - Manages broadcast channel lifecycle with proper cleanup
 * - Provides visibility state for the sync button
 * - Uses benefit guide hash URL as channel name for isolation
 *
 * @return {Object} Hook return object containing sync state and functions
 * @return {boolean} return.hasOtherTabs - Whether other tabs with same benefit guide are detected
 * @return {boolean} return.isChannelReady - Whether the broadcast channel is ready
 * @return {boolean} return.isSyncCooldown - Whether sync is currently in cooldown period
 * @return {number} return.cooldownTimeLeft - Seconds remaining in cooldown period
 * @return {boolean} return.showSyncButton - Whether to show the sync button (combines other conditions)
 * @return {Function} return.syncWithOtherTabs - Function to manually sync data to other tabs
 * @return {Function} return.requestFocusOnExistingTab - Function to request focus on existing tab
 */
const usePlanRecommenderTabSync = () => {
  const dispatch = useAppDispatch();
  const benguide = useBenguide();
  const { plans, answers, questions } = useAppSelector(
    (state) => state.planRecommender
  );

  const [hasOtherTabs, setHasOtherTabs] = useState(false);
  const [isChannelReady, setIsChannelReady] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<number>(0);
  const [isSyncCooldown, setIsSyncCooldown] = useState(false);
  const [cooldownTimeLeft, setCooldownTimeLeft] = useState<number>(0);
  const [focusRequestPending, setFocusRequestPending] = useState(false);

  const channelRef = useRef<BroadcastChannel | null>(null);
  const tabIdRef = useRef<string>(
    `tab_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`
  );
  const presenceIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const otherTabsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastPresenceCheckRef = useRef<number>(0);

  // Get channel name from benefit guide hash
  const channelName = benguide?.urlHash
    ? `plan_recommender_${benguide.urlHash}`
    : null;

  // Handle incoming sync data
  const handleIncomingSync = useCallback(
    (syncData: SyncData) => {
      // Only apply sync if it's newer than our last sync
      if (syncData.timestamp > lastSyncTime) {
        try {
          // Update chatbot context with plan recommender data
          if (benguide?.urlHash) {
            dispatch(
              setPlanRecommenderContext({
                sessionId: benguide.urlHash,
                planRecommenderContent: {
                  plans: syncData.plans,
                  answers: syncData.answers,
                  questions: syncData.questions,
                  lastUpdated: syncData.timestamp,
                },
              })
            );
            dispatch(addMessage({ ...syncData.actionMessage } as any));
            setLastSyncTime(syncData?.timestamp);
          }
        } catch (error) {
          console.error('Failed to apply sync data:', error);
        }
      }
    },
    [dispatch, lastSyncTime, benguide?.urlHash]
  );

  // Handle focus requests
  const handleFocusRequest = useCallback(
    (focusData: FocusRequestData) => {
      if (focusData.requestingTabId !== tabIdRef.current) {
        // This is a request from another tab, focus this window
        window.focus();

        // Send focus response
        if (channelRef.current && benguide?.urlHash) {
          const responseData: FocusResponseData = {
            type: 'FOCUS_RESPONSE',
            timestamp: Date.now(),
            benguideHash: benguide.urlHash,
            respondingTabId: tabIdRef.current,
          };

          try {
            channelRef.current.postMessage(responseData);
          } catch (error) {
            console.error('Failed to send focus response:', error);
          }
        }
      }
    },
    [benguide?.urlHash]
  );

  // Handle tab presence signals
  const handleTabPresence = useCallback((presenceData: TabPresenceData) => {
    if (presenceData.tabId !== tabIdRef.current) {
      setHasOtherTabs(true);
      lastPresenceCheckRef.current = Date.now();

      // Reset the timeout for detecting when other tabs close
      if (otherTabsTimeoutRef.current) {
        clearTimeout(otherTabsTimeoutRef.current);
      }

      // If we don't hear from other tabs for 10 seconds, assume they're closed
      otherTabsTimeoutRef.current = setTimeout(() => {
        setHasOtherTabs(false);
      }, 10000);
    }
  }, []);

  // Start broadcasting presence
  const startPresenceBroadcasting = useCallback(() => {
    if (!channelRef.current || !benguide?.urlHash) return;

    const broadcastPresence = () => {
      if (channelRef.current && benguide?.urlHash) {
        const presenceData: TabPresenceData = {
          type: 'TAB_PRESENCE',
          timestamp: Date.now(),
          tabId: tabIdRef.current,
          benguideHash: benguide.urlHash,
        };

        try {
          channelRef.current.postMessage(presenceData);
        } catch (error) {
          console.error('Failed to broadcast presence:', error);
        }
      }
    };

    // Broadcast immediately and then every 3 seconds
    broadcastPresence();
    presenceIntervalRef.current = setInterval(broadcastPresence, 3000);
  }, [benguide?.urlHash]);

  // Stop broadcasting presence
  const stopPresenceBroadcasting = useCallback(() => {
    if (presenceIntervalRef.current) {
      clearInterval(presenceIntervalRef.current);
      presenceIntervalRef.current = null;
    }
    if (otherTabsTimeoutRef.current) {
      clearTimeout(otherTabsTimeoutRef.current);
      otherTabsTimeoutRef.current = null;
    }
  }, []);

  // Initialize broadcast channel
  useEffect(() => {
    if (!channelName || !window.BroadcastChannel) {
      console.warn(
        'Broadcast Channel API not supported or no benefit guide hash available'
      );
      return;
    }

    try {
      const channel = new BroadcastChannel(channelName);
      channelRef.current = channel;
      setIsChannelReady(true);

      // Handle incoming messages
      const handleMessage = (
        event: MessageEvent<
          SyncData | TabPresenceData | FocusRequestData | FocusResponseData
        >
      ) => {
        const { data } = event;

        if (!data || data.benguideHash !== benguide.urlHash) {
          return; // Ignore messages from different benefit guides
        }

        switch (data.type) {
          case 'PLAN_RECOMMENDER_SYNC':
            handleIncomingSync(data);
            break;
          case 'TAB_PRESENCE':
            handleTabPresence(data);
            break;
          case 'FOCUS_REQUEST':
            handleFocusRequest(data);
            break;
          case 'FOCUS_RESPONSE':
            // Handle focus response if we're waiting for one
            if (
              focusRequestPending &&
              data.respondingTabId !== tabIdRef.current
            ) {
              setFocusRequestPending(false);
            }
            break;
        }
      };

      channel.addEventListener('message', handleMessage);

      // Start presence broadcasting
      startPresenceBroadcasting();

      return () => {
        channel.removeEventListener('message', handleMessage);
        channel.close();
        setIsChannelReady(false);
        stopPresenceBroadcasting();
      };
    } catch (error) {
      console.error('Failed to initialize broadcast channel:', error);
    }
  }, [
    channelName,
    benguide?.urlHash,
    handleIncomingSync,
    handleTabPresence,
    handleFocusRequest,
    startPresenceBroadcasting,
    stopPresenceBroadcasting,
    focusRequestPending,
  ]);

  // Start cooldown timer
  const startCooldown = useCallback(() => {
    setIsSyncCooldown(true);
    setCooldownTimeLeft(30);

    const interval = setInterval(() => {
      setCooldownTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setIsSyncCooldown(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // Request focus on existing tab
  const requestFocusOnExistingTab = useCallback(() => {
    return new Promise<boolean>((resolve) => {
      if (!channelRef.current || !benguide?.urlHash || !hasOtherTabs) {
        resolve(false);
        return;
      }

      setFocusRequestPending(true);

      const focusRequest: FocusRequestData = {
        type: 'FOCUS_REQUEST',
        timestamp: Date.now(),
        benguideHash: benguide.urlHash,
        requestingTabId: tabIdRef.current,
      };

      try {
        channelRef.current.postMessage(focusRequest);

        // Wait for response with timeout
        const timeout = setTimeout(() => {
          setFocusRequestPending(false);
          resolve(false);
        }, 1000);

        // Check for response
        const checkResponse = () => {
          if (!focusRequestPending) {
            clearTimeout(timeout);
            resolve(true);
          } else {
            setTimeout(checkResponse, 50);
          }
        };

        setTimeout(checkResponse, 50);
      } catch (error) {
        console.error('Failed to send focus request:', error);
        setFocusRequestPending(false);
        resolve(false);
      }
    });
  }, [benguide?.urlHash, hasOtherTabs, focusRequestPending]);

  // Manual sync function (called when button is clicked)
  const syncWithOtherTabs = useCallback(() => {
    if (!channelRef.current || !benguide?.urlHash || !plans.length) {
      console.warn('Cannot sync: channel not ready or no data to sync');
      return;
    }

    // Create a system notification
    const systemMessage: Omit<
      ChatMessage,
      'id' | 'timestamp' | 'createdAt' | 'updatedAt'
    > = {
      sender: Sender.BOT,
      content: `Here's the recommended plan for you, along with the <a href="PLAN_RECOMMENDER" style="text-decoration: underline; cursor: pointer;">full report.</a>`,
      status: MessageStatus.COMPLETED,
    };

    const syncData: SyncData = {
      type: 'PLAN_RECOMMENDER_SYNC',
      timestamp: Date.now(),
      plans,
      answers,
      questions,
      benguideHash: benguide.urlHash,
      actionMessage: systemMessage,
    };

    try {
      channelRef.current.postMessage(syncData);
      setLastSyncTime(syncData.timestamp);

      // Update our own context when manually syncing
      if (benguide?.urlHash) {
        dispatch(
          setPlanRecommenderContext({
            sessionId: benguide.urlHash,
            planRecommenderContent: {
              plans,
              answers,
              questions,
              lastUpdated: syncData.timestamp,
            },
          })
        );

        dispatch(addMessage({ ...systemMessage } as any));
      }
    } catch (error) {
      console.error('Failed to sync data:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans, answers, questions, benguide?.urlHash, dispatch, startCooldown]);

  // Note: Removed auto-sync - only manual sync on button click

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPresenceBroadcasting();
    };
  }, [stopPresenceBroadcasting]);

  return {
    // State
    hasOtherTabs,
    isChannelReady,
    isSyncCooldown,
    cooldownTimeLeft,
    showSyncButton:
      hasOtherTabs && isChannelReady && plans.length > 0 && !isSyncCooldown,

    // Actions
    syncWithOtherTabs,
    requestFocusOnExistingTab,
  };
};

export default usePlanRecommenderTabSync;
